import React from 'react'
import { useNavigate } from '@tanstack/react-router'
import { checkEmail, checkProviders, isApiError, tokenStore } from '../api'
import { MFA_LOGIN_DISABLED_MESSAGE } from '../utils/login-method-messages'
import { isValidEmail } from '../utils/validation'
import { Button } from './Button'
import { FormError } from './FormError'
import { Input } from './Input'
import { useToast } from './Toast'

const OTP_SENT_MESSAGE = "We've sent a 6-digit code to your email."

/**
 * Client-side email entry form for the login page.
 *
 * A single email identity can sign in with email + authenticator no matter
 * which other providers (Google/Microsoft) it's also linked to — they all
 * resolve to the same account. The form first reads the account-wide login
 * methods via `POST /auth/check-providers`; if the tenant admin has disabled
 * email + authenticator it blocks and points the user at SSO. Otherwise it
 * calls `POST /auth/check-email`, which drives sign-in:
 * - email not yet verified → an OTP was sent → go to the email verification step.
 * - email verified → store the short-lived `mfaToken` → TOTP step.
 */
export function EmailLoginForm() {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const [email, setEmail] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const emailIsValid = isValidEmail(email)

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setError(null)
    setEmail(event.target.value)
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!emailIsValid || submitting) return

    const trimmedEmail = email.trim()
    setSubmitting(true)
    setError(null)

    try {
      // Respect the account-wide login-method setting: if the tenant admin has
      // disabled email + authenticator sign-in, block here and point the user
      // at SSO. Enforcement is UI-side — the sign-in endpoints don't reject a
      // disabled method. Unknown emails default to enabled, so this never
      // reveals whether an account exists.
      const { loginMethods } = await checkProviders(trimmedEmail)
      if (!loginMethods.mfaLoginEnabled) {
        setError(MFA_LOGIN_DISABLED_MESSAGE)
        setSubmitting(false)
        return
      }

      const result = await checkEmail(trimmedEmail)

      tokenStore.setPendingEmail(trimmedEmail)

      if (result.emailVerified) {
        // Returning user — stash the single-use mfaToken and head to TOTP.
        tokenStore.setMfaToken(result.mfaToken)
        navigate({ to: '/login/verify-app' })
      } else {
        // First-time user — a 6-digit OTP was just emailed.
        showToast(OTP_SENT_MESSAGE, 'success')
        navigate({ to: '/login/verify-email', search: { email: trimmedEmail } })
      }
      // Navigation unmounts this form; leave `submitting` set to keep it disabled.
    } catch (err) {
      // Genuine failure (network, validation, rate limit) — surface it and stay.
      setError(isApiError(err) ? err.message : 'Something went wrong. Please try again.')
      setSubmitting(false)
    }
  }

  return (
    <form className="flex flex-col gap-4" id="form-login-email" onSubmit={handleSubmit}>
      <div>
        <Input
          type="email"
          id="input-email"
          label="Email address"
          placeholder="you@company.com"
          value={email}
          disabled={submitting}
          hasError={!!error}
          onChange={handleChange}
        />

        {error && <FormError id="alert-login-error">{error}</FormError>}
      </div>

      <Button type="submit" id="btn-login-submit" disabled={!emailIsValid || submitting}>
        {submitting ? 'Checking…' : 'Continue with email'}
      </Button>
    </form>
  )
}
